import ActionButton from "@/components/buttons/ActionButton";
import LabelWithPopover from "@/components/form/labels/LabelWithPopover";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import AntIcon from "@/components/icons/AntIcon";
import { InputNumber } from "@/components/InputNumber";
import { selectStandardProps } from "@/utils/componentUtils";
import { validations } from "@/utils/validationUtils";
import { Alert, Button, Card, Checkbox, Col, Divider, Form, Input, Row, Select, Switch } from "antd";
import { FormInstance } from "antd/lib/form";
import { useTranslation } from "react-i18next";
import { rowGutter } from "../../../../../../../common/constants";
import { ClientType } from "../../../../../../client/enums";
import { BuildingState, BuildingType, HouseholdEquipmentType, Liability, NearbyBuildingType } from "../../../enums";
import { RealtyCalc, RealtyCalcHouseholdInsuranceData } from "../../../types";
import { NEARBY_BUILDING_TYPES_WITH_AREA, NEARBY_BUILDING_TYPES_WITH_VOLUME } from "../../../utils";

const colSpan = 8;

interface Props {
  form: FormInstance<RealtyCalc>;
  policyHolderType?: ClientType;
}

export const RealtyCalcHouseholdRealtySection = ({ form, policyHolderType }: Props) => {
  const { t } = useTranslation();

  const type = Form.useWatch(["generalBuildingData", "type"], form);
  const isHouse = type === BuildingType.HOUSE || type === BuildingType.RECREATIONAL_BUILDING;
  const realtyInsuranceEnabled = Form.useWatch(["realtyInsuranceEnabled"], form);
  const householdInsuranceEnabled = Form.useWatch(["householdInsuranceEnabled"], form);
  const nearbyBuildingReinsurances = Form.useWatch(
    (values) => values.realtyInsuranceData?.nearbyBuildingReinsurances,
    form,
  );
  const state = Form.useWatch(["generalBuildingData", "state"], form);
  const permanentlyOccupied = Form.useWatch(["generalBuildingData", "permanentlyOccupied"], form);
  const liability = Form.useWatch(["liabilityInsuranceData", "liability"], form);

  const handleRealtyInsuranceEnabledChange = (checked: boolean) => {
    if (checked) {
      const householdInsuranceData = form.getFieldValue("householdInsuranceData") as RealtyCalcHouseholdInsuranceData;
      if (householdInsuranceData?.equipmentReinsurances) {
        form.setFieldsValue({
          householdInsuranceData: {
            equipmentReinsurances: householdInsuranceData.equipmentReinsurances.filter(
              (e) => e?.type !== HouseholdEquipmentType.STRUCTURAL_COMPONENTS,
            ),
          },
        });
      }
    } else {
      form.setFieldsValue({
        generalInsuranceData: { loanReinsurance: false },
      });
    }
  };

  return (
    <Card type="inner" className="card-box" title={t("calc.realty.sections.realtyAndHouseholdInsurance")}>
      <Row gutter={rowGutter} className="margin-bottom-large">
        <Col span={12}>
          <Row>
            <Col flex="170px" className="bold-text" style={{ marginTop: 5 }}>
              {t("calc.realty.attrs.realtyInsuranceData._label")}:
            </Col>

            <Col span={15}>
              <Form.Item
                name="realtyInsuranceEnabled"
                valuePropName="checked"
                dependencies={["householdInsuranceEnabled"]}
                rules={[
                  validations.notFalseIfOtherFalse(
                    ["householdInsuranceEnabled"],
                    t("calc.realty.attrs.householdInsuranceData._label"),
                  ),
                ]}
                initialValue={false}
              >
                <Switch
                  checkedChildren={<AntIcon type="check" />}
                  unCheckedChildren={<AntIcon type="close" />}
                  onChange={handleRealtyInsuranceEnabledChange}
                />
              </Form.Item>
            </Col>
          </Row>

          {realtyInsuranceEnabled && (
            <>
              <Divider className="divider-subheader">{t("calc.realty.sections.realtyInsurance")}</Divider>

              <Row gutter={rowGutter}>
                <Col span={colSpan + (isHouse ? 2 : 0)} style={{ marginTop: isHouse ? "-20px" : undefined }}>
                  <Form.Item
                    name={["realtyInsuranceData", "insuranceAmount"]}
                    label={
                      isHouse ? (
                        <LabelWithTooltip
                          tooltip={t("calc.realty.helpers.realtyInsuranceAmountDesc")}
                          label={t("calc.realty.attrs.realtyInsuranceData.insuranceAmountWithNearbyBuildings")}
                        />
                      ) : (
                        t("calc.realty.attrs.realtyInsuranceData.insuranceAmount")
                      )
                    }
                    rules={[validations.notNull, validations.minNumber(1), validations.multipleOf100]}
                  >
                    <InputNumber addon="euro" step={100} />
                  </Form.Item>
                </Col>

                <Col span={colSpan}>
                  <Form.Item
                    name={["realtyInsuranceData", "expertTestimony"]}
                    className="form-item-without-label"
                    valuePropName="checked"
                    rules={[validations.none]}
                    initialValue={false}
                  >
                    <Checkbox>{t("calc.realty.attrs.realtyInsuranceData.expertTestimony")}</Checkbox>
                  </Form.Item>
                </Col>
              </Row>

              {isHouse && (
                <>
                  <Divider className="divider-subheader">
                    {t("calc.realty.sections.realtyReinsurances")}
                    <LabelWithPopover
                      popoverTitle={t("calc.realty.helpers.nearbyBuildingDesc.title")}
                      popoverContent={
                        <table className="data-table-view wide-margins" style={{ maxWidth: 500 }}>
                          <tbody>
                            {[1, 2, 3, 4, 5, 6, 7, 8].map((value) => (
                              <tr key={value}>
                                <td>{t("calc.realty.helpers.nearbyBuildingDesc.institution" + value)}</td>
                                <td>{t("calc.realty.helpers.nearbyBuildingDesc.value" + value)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      }
                      className="margin-left-tiny"
                    />
                  </Divider>

                  <Form.List name={["realtyInsuranceData", "nearbyBuildingReinsurances"]}>
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map((field) => {
                          const nearbyBuildingType = nearbyBuildingReinsurances?.[field.name]?.type;

                          return (
                            <Card
                              key={field.key}
                              size="small"
                              className="margin-bottom-small"
                              title={`${t("calc.realty.helpers.nearbyBuilding")} ${field.name + 1}`}
                              extra={
                                <Button
                                  onClick={() => remove(field.name)}
                                  icon={<AntIcon color="red" type="delete" />}
                                  variant="text"
                                  color="red"
                                />
                              }
                            >
                              <Row gutter={rowGutter}>
                                <Col span={colSpan}>
                                  <Form.Item
                                    name={[field.name, "type"]}
                                    label={t("calc.realty.enums.nearbyBuildingType._label")}
                                    rules={[validations.notNull]}
                                  >
                                    <Select
                                      {...selectStandardProps}
                                      options={Object.keys(NearbyBuildingType).map((type) => ({
                                        value: type,
                                        label: t("calc.realty.enums.nearbyBuildingType." + type),
                                      }))}
                                    />
                                  </Form.Item>
                                </Col>

                                <Col span={colSpan}>
                                  <Form.Item
                                    name={[field.name, "insuranceAmount"]}
                                    label={t(
                                      "calc.realty.attrs.realtyInsuranceData.nearbyBuildingReinsurances.insuranceAmount",
                                    )}
                                    rules={[validations.notNull, validations.minNumber(1), validations.multipleOf100]}
                                  >
                                    <InputNumber addon="euro" step={100} />
                                  </Form.Item>
                                </Col>

                                {nearbyBuildingType && NEARBY_BUILDING_TYPES_WITH_AREA.includes(nearbyBuildingType) && (
                                  <Col span={colSpan - 1}>
                                    <Form.Item
                                      name={[field.name, "floorArea"]}
                                      label={t(
                                        "calc.realty.attrs.realtyInsuranceData.nearbyBuildingReinsurances.floorArea",
                                      )}
                                      rules={[validations.notNull, validations.minNumber(1)]}
                                    >
                                      <InputNumber addon="area" />
                                    </Form.Item>
                                  </Col>
                                )}

                                {nearbyBuildingType &&
                                  NEARBY_BUILDING_TYPES_WITH_VOLUME.includes(nearbyBuildingType) && (
                                    <Col span={colSpan - 1}>
                                      <Form.Item
                                        name={[field.name, "volume"]}
                                        label={t(
                                          "calc.realty.attrs.realtyInsuranceData.nearbyBuildingReinsurances.volume",
                                        )}
                                        rules={[validations.minNumber(1)]}
                                      >
                                        <InputNumber addon="volume" />
                                      </Form.Item>
                                    </Col>
                                  )}

                                {nearbyBuildingType === NearbyBuildingType.OTHER && (
                                  <Col span={colSpan * 2}>
                                    <Form.Item
                                      name={[field.name, "otherDesc"]}
                                      label={t(
                                        "calc.realty.attrs.realtyInsuranceData.nearbyBuildingReinsurances.otherDesc",
                                      )}
                                      rules={[validations.notBlank, validations.size(1, 64)]}
                                    >
                                      <Input />
                                    </Form.Item>
                                  </Col>
                                )}

                                {nearbyBuildingType === NearbyBuildingType.DETACHED_GARAGE && (
                                  <Col span={colSpan * 3}>
                                    <Form.Item
                                      name={[field.name, "placeOfGarageSameAsBuilding"]}
                                      valuePropName="checked"
                                      initialValue={true}
                                      rules={[validations.none]}
                                      noStyle
                                    >
                                      <Checkbox>
                                        <LabelWithPopover
                                          label={t(
                                            "calc.realty.attrs.realtyInsuranceData.nearbyBuildingReinsurances.placeOfGarageSameAsBuilding",
                                          )}
                                          popoverTitle={t("calc.realty.helpers.placeOfGarageSameAsBuildingDesc.title")}
                                          popoverContent={
                                            <div style={{ maxWidth: 550 }}>
                                              <table className="data-table-view wide-margins">
                                                <tbody>
                                                  {[1, 2, 3].map((value) => (
                                                    <tr key={value}>
                                                      <td>
                                                        {t(
                                                          "calc.realty.helpers.placeOfGarageSameAsBuildingDesc.institution" +
                                                            value,
                                                        )}
                                                      </td>
                                                      <td>
                                                        {t(
                                                          "calc.realty.helpers.placeOfGarageSameAsBuildingDesc.value" +
                                                            value,
                                                        )}
                                                      </td>
                                                    </tr>
                                                  ))}
                                                </tbody>
                                              </table>
                                            </div>
                                          }
                                        />
                                      </Checkbox>
                                    </Form.Item>
                                  </Col>
                                )}
                              </Row>
                            </Card>
                          );
                        })}

                        <ActionButton icon="plus" className="margin-top-small" onClick={() => add()}>
                          {t("calc.realty.actions.nearbyBuildingAdd")}
                        </ActionButton>

                        {fields?.length > 0 && (
                          <Alert
                            title={t("calc.realty.helpers.nearbyBuildingInsuranceAmount")}
                            type="info"
                            showIcon
                            className="margin-top-medium"
                            closable
                          />
                        )}
                      </>
                    )}
                  </Form.List>
                </>
              )}
            </>
          )}
        </Col>

        <Col span={12}>
          <Row gutter={rowGutter}>
            <Col flex="170px" className="bold-text" style={{ marginTop: "5px" }}>
              {t("calc.realty.attrs.householdInsuranceData._label")}:
            </Col>

            <Col span={15}>
              <Form.Item
                name="householdInsuranceEnabled"
                valuePropName="checked"
                dependencies={["realtyInsuranceEnabled"]}
                rules={[
                  validations.notFalseIfOtherFalse(
                    ["realtyInsuranceEnabled"],
                    t("calc.realty.attrs.realtyInsuranceData._label"),
                  ),
                ]}
                initialValue={false}
              >
                <Switch
                  disabled={
                    state === BuildingState.UNDER_CONSTRUCTION ||
                    policyHolderType === ClientType.SELF_EMPLOYED ||
                    policyHolderType === ClientType.LEGAL
                  }
                  checkedChildren={<AntIcon type="check" />}
                  unCheckedChildren={<AntIcon type="close" />}
                />
              </Form.Item>
            </Col>
          </Row>

          {state === BuildingState.UNDER_CONSTRUCTION && (
            <Alert title={t("calc.realty.helpers.householdInsuranceDisabledBuildingType")} type="info" showIcon />
          )}

          {(policyHolderType === ClientType.SELF_EMPLOYED || policyHolderType === ClientType.LEGAL) && (
            <Alert title={t("calc.realty.helpers.householdInsuranceDisabledClientType")} type="info" showIcon />
          )}

          {householdInsuranceEnabled && (
            <>
              <Divider className="divider-subheader">{t("calc.realty.sections.householdInsurance")}</Divider>

              <Row gutter={rowGutter}>
                <Col span={colSpan}>
                  <Form.Item
                    name={["householdInsuranceData", "insuranceAmount"]}
                    label={t("calc.realty.attrs.householdInsuranceData.insuranceAmount")}
                    rules={[validations.notNull, validations.minNumber(1), validations.multipleOf100]}
                  >
                    <InputNumber addon="euro" step={100} />
                  </Form.Item>
                </Col>
              </Row>

              <Divider className="divider-subheader" style={{ marginTop: "16px" }}>
                {t("calc.realty.sections.additionalHouseholdAmounts")}
              </Divider>

              <Row gutter={rowGutter}>
                <Col span={colSpan}>
                  <Form.Item
                    name={["householdInsuranceData", "electronicsInsuranceAmount"]}
                    label={
                      <LabelWithTooltip
                        tooltip={t("calc.realty.helpers.electronicsInsuranceAmountDesc")}
                        label={t("calc.realty.attrs.householdInsuranceData.electronicsInsuranceAmount")}
                      />
                    }
                    rules={[
                      validations.minNumber(1),
                      validations.maxNumber(permanentlyOccupied ? 20_000 : 2_000),
                      validations.multipleOf100,
                    ]}
                  >
                    <InputNumber addon="euro" step={100} />
                  </Form.Item>
                </Col>

                <Col span={colSpan}>
                  <Form.Item
                    name={["householdInsuranceData", "artworksInsuranceAmount"]}
                    label={
                      <LabelWithTooltip
                        tooltip={t("calc.realty.helpers.artworksInsuranceAmountDesc")}
                        label={t("calc.realty.attrs.householdInsuranceData.artworksInsuranceAmount")}
                      />
                    }
                    rules={[validations.minNumber(1), validations.maxNumber(10_000), validations.multipleOf100]}
                  >
                    <InputNumber addon="euro" step={100} disabled={!permanentlyOccupied} />
                  </Form.Item>
                </Col>

                <Col span={colSpan}>
                  <Form.Item
                    name={["householdInsuranceData", "valuablesInsuranceAmount"]}
                    label={
                      <LabelWithTooltip
                        tooltip={t("calc.realty.helpers.valuablesInsuranceAmountDesc")}
                        label={t("calc.realty.attrs.householdInsuranceData.valuablesInsuranceAmount")}
                      />
                    }
                    rules={[validations.minNumber(1), validations.maxNumber(10_000), validations.multipleOf100]}
                  >
                    <InputNumber addon="euro" step={100} disabled={!permanentlyOccupied} />
                  </Form.Item>
                </Col>
              </Row>

              <Alert
                title={t("calc.realty.helpers.additionalInsuranceAmount")}
                type="info"
                showIcon
                className="margin-top-medium"
                closable
              />

              <Divider className="divider-subheader" style={{ marginTop: "16px" }}>
                {t("calc.realty.sections.equipmentReinsurances")}
                <LabelWithPopover
                  popoverTitle={t("calc.realty.helpers.householdEquipmentDesc.title")}
                  popoverContent={
                    <table className="data-table-view wide-margins" style={{ maxWidth: "500px" }}>
                      <tbody>
                        {[1, 2, 3, 4, 5, 6].map((value) => (
                          <tr key={value}>
                            <td>{t("calc.realty.helpers.householdEquipmentDesc.institution" + value)}</td>
                            <td>{t("calc.realty.helpers.householdEquipmentDesc.value" + value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  }
                  className="margin-left-tiny"
                />
              </Divider>

              <Form.List name={["householdInsuranceData", "equipmentReinsurances"]}>
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field) => (
                      <Card
                        key={field.key}
                        size="small"
                        className="margin-bottom-small"
                        title={`${t("calc.realty.helpers.reinsurance")} ${field.name + 1}`}
                        extra={
                          <Button
                            onClick={() => remove(field.name)}
                            icon={<AntIcon color="red" type="delete" />}
                            variant="text"
                            color="red"
                          />
                        }
                      >
                        <Row gutter={rowGutter}>
                          <Col span={colSpan}>
                            <Form.Item
                              name={[field.name, "type"]}
                              label={t("calc.realty.enums.householdEquipmentType._label")}
                              rules={[validations.notNull]}
                            >
                              <Select
                                {...selectStandardProps}
                                options={Object.keys(HouseholdEquipmentType)
                                  .filter((type) =>
                                    realtyInsuranceEnabled
                                      ? type !== HouseholdEquipmentType.STRUCTURAL_COMPONENTS
                                      : true,
                                  )
                                  .map((type) => ({
                                    value: type,
                                    label: t("calc.realty.enums.householdEquipmentType." + type),
                                  }))}
                              />
                            </Form.Item>
                          </Col>

                          <Col span={colSpan}>
                            <Form.Item
                              name={[field.name, "insuranceAmount"]}
                              label={t(
                                "calc.realty.attrs.householdInsuranceData.equipmentReinsurances.insuranceAmount",
                              )}
                              rules={[
                                validations.notNull,
                                validations.minNumber(1),
                                validations.maxNumber(50000),
                                validations.multipleOf100,
                              ]}
                            >
                              <InputNumber addon="euro" step={100} />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Card>
                    ))}

                    <ActionButton
                      icon="plus"
                      className="margin-top-small"
                      disabled={fields.length >= 10}
                      onClick={() => add()}
                    >
                      {t("calc.realty.actions.householdEquipmentAdd")}
                    </ActionButton>
                  </>
                )}
              </Form.List>
            </>
          )}
        </Col>
      </Row>

      <Divider className="divider-subheader" titlePlacement="start">
        {t("calc.realty.attrs.liabilityInsuranceData.liabilityFromRealtyAndHousehold")}
      </Divider>

      <Row gutter={rowGutter} className="margin-top-medium">
        <Col span={6}>
          <Form.Item
            name={["liabilityInsuranceData", "liability"]}
            label={
              <LabelWithPopover
                label={t("calc.realty.enums.liability._label")}
                popoverTitle={t("calc.realty.helpers.liabilityDesc.title")}
                popoverContent={
                  <table className="data-table-view wide-margins">
                    <tbody>
                      {[1, 2, 3, 4, 5, 6, 7].map((value) => (
                        <tr key={value}>
                          <td>{t("calc.realty.helpers.liabilityDesc.institution" + value)}</td>
                          <td>{t("calc.realty.helpers.liabilityDesc.value" + value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                }
              />
            }
            rules={[validations.notNull]}
          >
            <Select
              {...selectStandardProps}
              options={Object.keys(Liability).map((liability) => ({
                value: liability,
                label: t("calc.realty.enums.liability." + liability),
              }))}
            />
          </Form.Item>
        </Col>

        {liability && liability !== Liability.NONE && (
          <Col span={colSpan}>
            <Form.Item
              name={["liabilityInsuranceData", "extendedLiability"]}
              className="form-item-without-label"
              valuePropName="checked"
              initialValue={false}
              rules={[validations.none]}
            >
              <Checkbox style={{ maxHeight: 22 }}>
                <LabelWithPopover
                  label={t("calc.realty.attrs.liabilityInsuranceData.extendedLiability")}
                  popoverTitle={t("calc.realty.helpers.extendedLiabilityDesc.title")}
                  popoverContent={
                    <div style={{ maxWidth: "550px" }}>
                      <table className="data-table-view wide-margins">
                        <tbody>
                          {[1, 2].map((value) => (
                            <tr key={value}>
                              <td>{t("calc.realty.helpers.extendedLiabilityDesc.institution" + value)}</td>
                              <td>{t("calc.realty.helpers.extendedLiabilityDesc.value" + value)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  }
                />
              </Checkbox>
            </Form.Item>
          </Col>
        )}
      </Row>
    </Card>
  );
};
